/**
 * CKpiCard Configuration
 *
 * Defines accent colors, trend indicators, and their styling.
 */

import { TrendingUp, TrendingDown, Minus } from "lucide-react";

// ============================================================================
// Accent Color Configuration
// ============================================================================

/** Available accent colors for the card */
export type AccentColor =
  | "indigo"
  | "emerald"
  | "violet"
  | "rose"
  | "amber"
  | "blue"
  | "cyan"
  | "teal"
  | "red"
  | "orange"
  | "pink"
  | "slate";

/** Color configuration for each accent color (icon only) */
export const accentColorConfig: Record<
  AccentColor,
  {
    iconGradient: string;
    iconText: string;
  }
> = {
  indigo: {
    iconGradient:
      "from-indigo-50 to-violet-50 dark:from-indigo-950 dark:to-violet-950",
    iconText: "text-indigo-600 dark:text-indigo-400",
  },
  emerald: {
    iconGradient:
      "from-emerald-50 to-teal-50 dark:from-emerald-950 dark:to-teal-950",
    iconText: "text-emerald-600 dark:text-emerald-400",
  },
  violet: {
    iconGradient:
      "from-violet-50 to-purple-50 dark:from-violet-950 dark:to-purple-950",
    iconText: "text-violet-600 dark:text-violet-400",
  },
  rose: {
    iconGradient: "from-rose-50 to-pink-50 dark:from-rose-950 dark:to-pink-950",
    iconText: "text-rose-600 dark:text-rose-400",
  },
  amber: {
    iconGradient:
      "from-amber-50 to-orange-50 dark:from-amber-950 dark:to-amber-950",
    iconText: "text-amber-600 dark:text-amber-400",
  },
  blue: {
    iconGradient:
      "from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950",
    iconText: "text-blue-600 dark:text-blue-400",
  },
  cyan: {
    iconGradient: "from-cyan-50 to-teal-50 dark:from-cyan-950 dark:to-teal-950",
    iconText: "text-cyan-600 dark:text-cyan-400",
  },
  teal: {
    iconGradient:
      "from-teal-50 to-emerald-50 dark:from-teal-950 dark:to-teal-950",
    iconText: "text-teal-600 dark:text-teal-400",
  },
  red: {
    iconGradient: "from-red-50 to-rose-50 dark:from-red-950 dark:to-rose-950",
    iconText: "text-red-600 dark:text-red-400",
  },
  orange: {
    iconGradient:
      "from-orange-50 to-amber-50 dark:from-orange-950 dark:to-amber-950",
    iconText: "text-orange-600 dark:text-orange-400",
  },
  pink: {
    iconGradient: "from-pink-50 to-rose-50 dark:from-pink-950 dark:to-rose-950",
    iconText: "text-pink-600 dark:text-pink-400",
  },
  slate: {
    iconGradient:
      "from-slate-50 to-gray-50 dark:from-slate-950 dark:to-gray-950",
    iconText: "text-slate-600 dark:text-slate-400",
  },
};

// ============================================================================
// Trend Configuration
// ============================================================================

/** Trend indicator configuration */
export interface CKpiCardTrend {
  /** Trend direction */
  direction: "up" | "down" | "neutral";
  /** Trend value (e.g., "12%", "+5") */
  value: string;
}

/** Trend color and icon configuration */
export const trendConfig = {
  up: {
    color: "text-emerald-600 dark:text-emerald-400",
    icon: TrendingUp,
  },
  down: {
    color: "text-red-600 dark:text-red-400",
    icon: TrendingDown,
  },
  neutral: {
    color: "text-slate-500 dark:text-slate-400",
    icon: Minus,
  },
};

// ============================================================================
// Badge Configuration
// ============================================================================

/** Badge variants for additional info display */
export type BadgeVariant = "success" | "warning" | "info" | "error" | "neutral";

/** Badge configuration for showing additional info next to the value */
export interface CKpiCardBadge {
  /** Badge text content */
  text: string;
  /** Badge color variant */
  variant: BadgeVariant;
}

/** Badge variant color configuration */
export const badgeVariantConfig: Record<BadgeVariant, string> = {
  success:
    "text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950",
  warning: "text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-950",
  info: "text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-950",
  error: "text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-950",
  neutral: "text-slate-600 bg-slate-100 dark:text-slate-400 dark:bg-slate-800",
};
